import { useEffect, useState } from "react";
import { Loader2, Save, ListChecks, Sparkles, PenLine, Globe, UploadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ServicesEditor } from "@/components/dashboard/ServicesEditor";
import { ServiceDocumentUpload } from "@/components/knowledge/ServiceDocumentUpload";
import { ServiceWebsiteImport } from "@/components/knowledge/ServiceWebsiteImport";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ServicesPricing() {
  const [cfg, setCfg] = useState(null);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/business").then((r) => {
      setCfg(r.data);
      const list = (r.data.service_list && r.data.service_list.length)
        ? r.data.service_list
        : (r.data.services || []).map((n) => ({ id: n, name: n, price: "", duration: "", description: "", enabled: true }));
      setServices(list);
    }).catch(() => {});
    api.get("/staff").then((r) => setStaff((r.data.staff || []).map((s) => ({ id: s.id, name: s.name })))).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const clean = services.filter((s) => s.name.trim());
      const r = await api.put("/business", { ...cfg, service_list: clean });
      setCfg(r.data);
      toast.success("Services saved — your AI updated instantly.");
    } catch (e) {
      toast.error("Could not save services");
    } finally { setSaving(false); }
  };

  if (!cfg) return <div className="flex items-center gap-2 text-slate-400 py-20 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;

  const activeCount = services.filter((s) => s.enabled && s.name.trim()).length;

  return (
    <div data-testid="services-view">
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><ListChecks className="h-6 w-6 text-blue-600" /> Services &amp; Pricing</h1>
        <Button data-testid="save-services-btn" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save &amp; update AI
        </Button>
      </div>
      <p className="text-slate-500 text-sm">Your AI only quotes the services you approve here — it never invents prices or durations. {activeCount} active.</p>

      {/* Import from website or upload → extract → review */}
      <div className="mt-6 max-w-3xl rounded-2xl border border-blue-200 bg-blue-50/40 p-5">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-blue-600" />
          <h2 className="font-semibold text-slate-900">Add services automatically</h2>
          <span className="text-[10px] font-bold bg-blue-600 text-white rounded-full px-2 py-0.5">FASTEST</span>
        </div>

        <p className="text-sm text-slate-500 mt-3 mb-2 font-medium flex items-center gap-1.5"><Globe className="h-4 w-4 text-blue-600" /> Import from your website</p>
        <p className="text-xs text-slate-500 mb-3">Paste your website URL and OperaMind reads your services &amp; prices automatically — review and add them below.</p>
        <ServiceWebsiteImport onAdd={(svcs) => setServices((prev) => [...prev, ...svcs])} />

        <div className="my-4 flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" /> OR <span className="h-px flex-1 bg-slate-200" /></div>

        <p className="text-sm text-slate-500 mb-1 font-medium flex items-center gap-1.5"><UploadCloud className="h-4 w-4 text-blue-600" /> Upload a menu or price list</p>
        <p className="text-xs text-slate-500 mb-3">Upload a menu, price list, brochure or catalogue and OperaMind extracts your services automatically. Files are also saved to your AI's knowledge.</p>
        <ServiceDocumentUpload onAdd={(svcs) => setServices((prev) => [...prev, ...svcs])} />
      </div>

      {/* Manual services */}
      <div className="mt-6 max-w-3xl">
        <div className="flex items-center gap-2 mb-3">
          <PenLine className="h-4 w-4 text-slate-500" />
          <h2 className="font-semibold text-slate-900">Manual services &amp; pricing</h2>
        </div>
        <ServicesEditor value={services} onChange={setServices} staff={staff} />
      </div>
    </div>
  );
}
